import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Archive, FileText, Plus, RotateCcw, Sparkles, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { PageFrame, SectionHeading } from '@/components/PageFrame'
import { Button } from '@/components/ui/button'
import { useConfirm } from '@/components/ui/confirm'
import { Badge, EmptyState, Panel, Skeleton } from '@/components/ui/display'
import {
  createTemplate,
  listTemplates,
  removeTemplate,
  updateTemplate,
  type TemplateDraft,
} from '@/data/templates'
import type { NoteTemplate } from '@/data/types'
import { cn } from '@/lib/utils'
import { TemplateEditor } from './TemplateEditor'

/**
 * The template library.
 *
 * Rows that open in place, exactly like a meeting row — not a dialog. A
 * template is edited against the other templates in the tenant ("do I already
 * have one that does this?"), and a modal is precisely the thing that hides
 * them while the question is being asked.
 */
export function TemplatesPage() {
  const queryClient = useQueryClient()
  const confirm = useConfirm()

  /** The row being edited, `'new'` while creating, null when the list is at rest. */
  const [editing, setEditing] = useState<string | 'new' | null>(null)

  const templates = useQuery({ queryKey: ['templates'], queryFn: listTemplates })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['templates'] })

  const create = useMutation({
    mutationFn: (draft: TemplateDraft) => createTemplate(draft),
    onSuccess: () => {
      void invalidate()
      setEditing(null)
    },
  })

  const update = useMutation({
    mutationFn: ({ id, draft }: { id: string; draft: Partial<TemplateDraft> }) =>
      updateTemplate(id, draft),
    onSuccess: () => {
      void invalidate()
      setEditing(null)
    },
  })

  const remove = useMutation({
    mutationFn: (id: string) => removeTemplate(id),
    onSuccess: () => void invalidate(),
  })

  const all = templates.data ?? []
  const active = all.filter((template) => template.isActive)
  const archived = all.filter((template) => !template.isActive)
  const auto = active.filter((template) => template.autoGenerate).length

  return (
    <PageFrame
      title="Templates"
      subtitle={
        templates.isPending
          ? undefined
          : `${auto} of ${active.length} run automatically`
      }
      actions={
        <Button
          size="sm"
          variant="primary"
          onClick={() => setEditing((current) => (current === 'new' ? null : 'new'))}
        >
          <Plus className="size-3.5" />
          New template
        </Button>
      }
    >
      {editing === 'new' ? (
        <Panel className="mt-3 border-[var(--color-border-strong)] bg-[var(--color-surface-sunken)] shadow-[inset_3px_0_0_var(--color-fg)]">
          <div className="px-3 py-2">
            <p className="text-xs font-medium text-[var(--color-fg)]">New template</p>
            <p className="mt-0.5 text-2xs text-[var(--color-fg-subtle)]">
              One template writes one note from one transcript.
            </p>
          </div>
          <TemplateEditor
            busy={create.isPending}
            onSave={(draft) => create.mutate(draft)}
            onCancel={() => setEditing(null)}
          />
        </Panel>
      ) : null}

      {templates.isPending ? (
        <div className="space-y-1 pt-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-12 w-full" />
          ))}
        </div>
      ) : active.length === 0 && archived.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No templates yet"
          description="A template is a saved instruction for turning a transcript into one note."
          action={
            <Button size="sm" variant="primary" onClick={() => setEditing('new')}>
              <Plus className="size-3.5" />
              New template
            </Button>
          }
        />
      ) : (
        <>
          {active.length > 0 ? (
            <>
              <SectionHeading>In use</SectionHeading>
              <Panel>
                {active.map((template) => (
                  <TemplateRow
                    key={template.id}
                    template={template}
                    expanded={editing === template.id}
                    busy={update.isPending || remove.isPending}
                    onToggle={() =>
                      setEditing((current) => (current === template.id ? null : template.id))
                    }
                    onSave={(draft) => update.mutate({ id: template.id, draft })}
                    onCancel={() => setEditing(null)}
                    onRemove={async () => {
                      const ok = await confirm({
                        title: template.isSystemDefault
                          ? `Archive “${template.name}”?`
                          : `Delete “${template.name}”?`,
                        body: template.isSystemDefault
                          ? 'It ships with the product, so it is archived rather than deleted. Notes already written from it keep their text.'
                          : 'Notes already written from it keep their text. New meetings will no longer offer it.',
                        confirmLabel: template.isSystemDefault ? 'Archive' : 'Delete',
                        tone: 'danger',
                      })
                      if (ok) remove.mutate(template.id)
                    }}
                  />
                ))}
              </Panel>
            </>
          ) : null}

          {archived.length > 0 ? (
            <>
              <SectionHeading meta="not offered on new meetings">Archived</SectionHeading>
              <Panel>
                {archived.map((template) => (
                  <div
                    key={template.id}
                    className="flex items-center gap-2.5 border-b border-[var(--color-border-subtle)] px-3 py-2 last:border-b-0"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-xs text-[var(--color-fg-muted)]">
                        {template.name}
                      </span>
                      <span className="block truncate text-2xs text-[var(--color-fg-subtle)]">
                        {template.description}
                      </span>
                    </span>
                    <Button
                      size="xs"
                      variant="ghost"
                      disabled={update.isPending}
                      onClick={() => update.mutate({ id: template.id, draft: { isActive: true } })}
                    >
                      <RotateCcw className="size-3" />
                      Restore
                    </Button>
                  </div>
                ))}
              </Panel>
            </>
          ) : null}
        </>
      )}
    </PageFrame>
  )
}

/**
 * One template, in three fixed zones — the same shape as a meeting row, so the
 * two lists read down as columns rather than as two different products.
 */
function TemplateRow({
  template,
  expanded,
  busy,
  onToggle,
  onSave,
  onCancel,
  onRemove,
}: {
  template: NoteTemplate
  expanded: boolean
  busy?: boolean
  onToggle: () => void
  onSave: (draft: Partial<TemplateDraft>) => void
  onCancel: () => void
  onRemove: () => void
}) {
  return (
    <div
      className={cn(
        'group border-b last:border-b-0',
        expanded
          ? 'relative -mt-px border-y border-[var(--color-border-strong)] bg-[var(--color-surface-sunken)] shadow-[inset_3px_0_0_var(--color-fg)] first:mt-0'
          : 'border-[var(--color-border-subtle)]'
      )}
    >
      <div
        className={cn(
          'flex items-center gap-2.5 px-2.5',
          expanded ? '' : 'hover:bg-[var(--color-surface-hover)]'
        )}
      >
        <span className="grid h-5 w-10 shrink-0 place-items-center" aria-hidden>
          {template.autoGenerate ? (
            <Sparkles className="size-3 text-[var(--color-fg-subtle)]" />
          ) : null}
        </span>

        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          className="flex min-w-0 flex-1 items-center py-2 text-left outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]/35"
        >
          <span className="min-w-0 flex-1">
            <span className="block truncate text-xs font-medium text-[var(--color-fg)]">
              {template.name}
            </span>
            <span className="mt-0.5 block truncate text-2xs text-[var(--color-fg-subtle)]">
              {template.description || 'No description'}
              {' · '}
              <span className="font-mono">{template.model}</span>
              {template.reasoningEffort === 'medium' ? '' : ` · ${template.reasoningEffort}`}
            </span>
          </span>
        </button>

        <div className="flex shrink-0 items-center gap-1.5">
          {template.shareByDefault ? <Badge tone="outline">External</Badge> : null}
          {template.isSystemDefault ? <Badge tone="neutral">Default</Badge> : null}
          <Button
            size="iconSm"
            variant="ghost"
            disabled={busy}
            aria-label={
              template.isSystemDefault ? `Archive ${template.name}` : `Delete ${template.name}`
            }
            className="hover:text-[var(--color-danger)]"
            onClick={onRemove}
          >
            {template.isSystemDefault ? (
              <Archive className="size-3" />
            ) : (
              <Trash2 className="size-3" />
            )}
          </Button>
        </div>
      </div>

      {expanded ? (
        <TemplateEditor template={template} busy={busy} onSave={onSave} onCancel={onCancel} />
      ) : null}
    </div>
  )
}
